import {html, svg, type HTMLTemplateResult, type SVGTemplateResult} from "lit";
import {formatTime} from "../../../../domain/gimmicks/stopwatch.state";
import type {SkinInput} from "./skin-input";

/** Segment names a–g of a seven-segment digit: a top, b top right, c bottom right, d bottom, e bottom left,
 *  f top left, g middle. */
export type Segment = 'a' | 'b' | 'c' | 'd' | 'e' | 'f' | 'g';

/** Which segments light up for each digit. */
export const SEGMENTS_BY_DIGIT: Readonly<Record<string, readonly Segment[]>> = {
    '0': ['a', 'b', 'c', 'd', 'e', 'f'],
    '1': ['b', 'c'],
    '2': ['a', 'b', 'g', 'e', 'd'],
    '3': ['a', 'b', 'g', 'c', 'd'],
    '4': ['f', 'g', 'b', 'c'],
    '5': ['a', 'f', 'g', 'c', 'd'],
    '6': ['a', 'f', 'g', 'e', 'c', 'd'],
    '7': ['a', 'b', 'c'],
    '8': ['a', 'b', 'c', 'd', 'e', 'f', 'g'],
    '9': ['a', 'b', 'c', 'd', 'f', 'g'],
};

/** Hexagon segments in a 40×70 cell. */
const SEGMENT_POINTS: Readonly<Record<Segment, string>> = {
    a: '6,3 34,3 38,7 34,11 6,11 2,7',
    b: '35,8 39,12 39,31 35,35 31,31 31,12',
    c: '35,36 39,40 39,59 35,63 31,59 31,40',
    d: '6,60 34,60 38,64 34,68 6,68 2,64',
    e: '5,36 9,40 9,59 5,63 1,59 1,40',
    f: '5,8 9,12 9,31 5,35 1,31 1,12',
    g: '6,31.5 34,31.5 38,35.5 34,39.5 6,39.5 2,35.5',
};
const ALL_SEGMENTS = Object.keys(SEGMENT_POINTS) as Segment[];
const DIGIT_WIDTH = 46;
const SEPARATOR_WIDTH = 16;

function digit(char: string, x: number, small: boolean): SVGTemplateResult {
    const lit = SEGMENTS_BY_DIGIT[char] ?? [];
    const scale = small ? 0.6 : 1;
    const y = small ? 28 : 0;
    return svg`
        <g transform=${`translate(${x} ${y}) scale(${scale})`}>
            ${ALL_SEGMENTS.map((segment) => svg`
                <polygon class=${lit.includes(segment) ? 'stopwatch-led__seg stopwatch-led__seg--on' : 'stopwatch-led__seg'}
                         points=${SEGMENT_POINTS[segment]}></polygon>`)}
        </g>`;
}

/**
 * "Retro-LED": seven-segment digits on a dark panel; lit segments in the accent with a neon glow, the unlit
 * ones faintly visible. `mm:ss,cc`, from one hour on `h:mm:ss,cc` — the panel grows with the digits.
 */
export function renderLedSkin({parts}: SkinInput): HTMLTemplateResult {
    const text = formatTime(parts);
    const commaAt = text.indexOf(',');
    let x = 8;
    const items: SVGTemplateResult[] = [];
    [...text].forEach((char, index) => {
        const small = index > commaAt;
        if (char === ':') {
            items.push(svg`<circle class="stopwatch-led__seg stopwatch-led__seg--on" cx=${x + 6} cy="22" r="3.5"></circle>
                           <circle class="stopwatch-led__seg stopwatch-led__seg--on" cx=${x + 6} cy="50" r="3.5"></circle>`);
            x += SEPARATOR_WIDTH;
        } else if (char === ',') {
            items.push(svg`<circle class="stopwatch-led__seg stopwatch-led__seg--on" cx=${x + 5} cy="66" r="3.5"></circle>`);
            x += SEPARATOR_WIDTH - 4;
        } else {
            items.push(digit(char, x, small));
            x += small ? DIGIT_WIDTH * 0.6 : DIGIT_WIDTH;
        }
    });
    const width = x + 6;

    return html`
        <svg class="stopwatch-led" viewBox=${`0 0 ${width} 82`} aria-hidden="true">
            ${svg`<rect class="stopwatch-led__panel" x="0" y="0" width=${width} height="82" rx="8"></rect>`}
            ${svg`<g transform="translate(0 6)">${items}</g>`}
        </svg>
    `;
}
